/**
 * Centralized Error Handling Service
 * Provides consistent error handling, logging, and user notification across the application
 */

class ErrorHandlingService {
    static errorListeners = [];
    static isInitialized = false;

    /**
     * Initialize the error handling service
     * Sets up global error listeners and configuration
     */
    static initialize() {
        if (this.isInitialized) return;

        // Global error handler for unhandled promise rejections
        window.addEventListener('unhandledrejection', (event) => {
            console.error('Unhandled promise rejection:', event.reason);
            this.handleError(event.reason, 'UNHANDLED_REJECTION');
        });

        // Global error handler for JavaScript errors
        window.addEventListener('error', (event) => {
            console.error('Global error:', event.error);
            this.handleError(event.error, 'GLOBAL_ERROR');
        });

        this.isInitialized = true;
    }

    /**
     * Register a callback to be called when errors occur
     * @param {Function} callback - Function to call with error details
     */
    static onError(callback) {
        if (typeof callback === 'function') {
            this.errorListeners.push(callback);
        }
    }

    /**
     * Remove an error listener
     * @param {Function} callback - The callback to remove
     */
    static removeErrorListener(callback) {
        const index = this.errorListeners.indexOf(callback);
        if (index > -1) {
            this.errorListeners.splice(index, 1);
        }
    }

    /**
     * Handle an error with proper logging and user notification
     * @param {Error|string} error - The error to handle
     * @param {string} context - Context where the error occurred
     * @param {Object} options - Additional options
     */
    static handleError(error, context = 'UNKNOWN', options = {}) {
        const errorInfo = this.normalizeError(error, context);

        // Log to console (will be removed in production)
        if (process.env.NODE_ENV === 'development') {
            console.error(`[${context}]`, errorInfo);
        }

        // Notify error listeners
        this.errorListeners.forEach(listener => {
            try {
                listener(errorInfo);
            } catch (listenerError) {
                console.error('Error in error listener:', listenerError);
            }
        });

        // Send to external logging service in production
        if (process.env.NODE_ENV === 'production') {
            this.logToExternalService(errorInfo);
        }

        return errorInfo;
    }

    /**
     * Normalize different error types to a consistent format
     * @param {Error|string|Object} error - The error to normalize
     * @param {string} context - Context information
     */
    static normalizeError(error, context) {
        const timestamp = new Date().toISOString();

        if (error instanceof Error) {
            return {
                message: error.message,
                stack: error.stack,
                name: error.name,
                context,
                timestamp,
                userAgent: navigator.userAgent,
                url: window.location.href
            };
        }

        if (typeof error === 'string') {
            return {
                message: error,
                stack: new Error().stack,
                name: 'StringError',
                context,
                timestamp,
                userAgent: navigator.userAgent,
                url: window.location.href
            };
        }

        if (error && typeof error === 'object') {
            return {
                message: error.message || 'Unknown error',
                stack: error.stack || new Error().stack,
                name: error.name || 'ObjectError',
                context,
                timestamp,
                userAgent: navigator.userAgent,
                url: window.location.href,
                ...error
            };
        }

        return {
            message: 'Unknown error occurred',
            stack: new Error().stack,
            name: 'UnknownError',
            context,
            timestamp,
            userAgent: navigator.userAgent,
            url: window.location.href
        };
    }

    /**
     * Create a user-friendly error message
     * @param {Object} errorInfo - Normalized error information
     * @returns {string} User-friendly error message
     */
    static getUserFriendlyMessage(errorInfo) {
        // Map common error patterns to user-friendly messages
        const errorPatterns = {
            'Network Error': 'Probleme de conexiune la internet. Verificați conexiunea și încercați din nou.',
            'Failed to fetch': 'Nu s-a putut conecta la server. Verificați conexiunea la internet.',
            '401': 'Sesiunea dvs. a expirat. Vă rugăm să vă autentificați din nou.',
            '403': 'Nu aveți permisiunea de a accesa această resursă.',
            '404': 'Resursa solicitată nu a fost găsită.',
            '500': 'Eroare internă de server. Vă rugăm să încercați din nou mai târziu.',
            'timeout': 'Operația a durat prea mult. Vă rugăm să încercați din nou.',
            'ValidationError': 'Datele introduse nu sunt valide. Verificați și încercați din nou.'
        };

        const message = errorInfo.message?.toLowerCase() || '';

        for (const [pattern, friendlyMessage] of Object.entries(errorPatterns)) {
            if (message.includes(pattern.toLowerCase())) {
                return friendlyMessage;
            }
        }

        // Default message
        return 'A apărut o eroare neașteptată. Vă rugăm să încercați din nou.';
    }

    /**
     * Handle API errors specifically
     * @param {Response} response - Fetch response object
     * @param {string} context - Context information
     */
    static async handleAPIError(response, context = 'API_CALL') {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

        try {
            const errorData = await response.json();
            if (errorData.message) {
                errorMessage = errorData.message;
            } else if (errorData.error) {
                errorMessage = errorData.error;
            } else if (errorData.detail) {
                errorMessage = errorData.detail;
            }
        } catch (parseError) {
            // If we can't parse the response, use the status text
        }

        const error = new Error(errorMessage);
        error.status = response.status;
        error.response = response;

        return this.handleError(error, context);
    }

    /**
     * Wrap async functions to handle errors automatically
     * @param {Function} asyncFunction - The async function to wrap
     * @param {string} context - Context for error handling
     */
    static wrapAsync(asyncFunction, context = 'ASYNC_OPERATION') {
        return async (...args) => {
            try {
                return await asyncFunction(...args);
            } catch (error) {
                this.handleError(error, context);
                throw error; // Re-throw so the caller can handle it too
            }
        };
    }

    /**
     * Send error to external logging service (placeholder for production)
     * @param {Object} errorInfo - Error information to log
     */
    static logToExternalService(errorInfo) {
        // This would be implemented with your preferred logging service
        // e.g., Sentry, LogRocket, etc.
        console.log('Would send to external logging service:', errorInfo);
    }

    /**
     * Clear all error listeners (useful for cleanup)
     */
    static clearListeners() {
        this.errorListeners = [];
    }
}

export default ErrorHandlingService;